import { TargetTypes } from "@/modules/targets/types";
import { createContext, useContext, useState, ReactNode } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useRanksWithTarget from "@/modules/targets/hooks/ranks-with-target.hook";

interface TargetMonths {
  m1: number;
  m2: number;
  m3: number;
}

interface RankTargets {
  rankUId: string;
  personalTarget: TargetMonths;
  minimumTarget: TargetMonths;
}

// Form schema for validation
const targetMonthsSchema = z.object({
  m1: z.number().min(0, "Month 1 must be 0 or greater"),
  m2: z.number().min(0, "Month 2 must be 0 or greater"),
  m3: z.number().min(0, "Month 3 must be 0 or greater"),
});

const rankTargetsSchema = z.object({
  rankUId: z.string(),
  personalTarget: targetMonthsSchema,
  minimumTarget: targetMonthsSchema,
});

const targetsFormSchema = z.object({
  ranksTargets: z.array(rankTargetsSchema),
});

export type TargetsFormData = z.infer<typeof targetsFormSchema>;

interface TargetsTemplateInterface {
  targetType: TargetTypes;
  setTargetType: (targetType: TargetTypes) => void;
  subDepartment: { uid: string; name: string; hasTarget: boolean } | null;
  setSubDepartment: (
    subDepartment: { uid: string; name: string; hasTarget: boolean } | null
  ) => void;
  form: UseFormReturn<TargetsFormData>;
  getRanksTargets: () => RankTargets[];
  validateForm: () => { isValid: boolean; errors: string[] };
}

const TargetsTemplateContext = createContext<
  TargetsTemplateInterface | undefined
>(undefined);

interface TargetsTemplateProviderProps {
  children: ReactNode;
}

export function TargetsTemplateProvider({
  children,
}: TargetsTemplateProviderProps) {
  const [targetType, setTargetType] = useState<TargetTypes>(TargetTypes.GMV);
  const [subDepartment, setSubDepartment] = useState<{
    uid: string;
    name: string;
    hasTarget: boolean;
  } | null>(null);

  const { ranksWithTarget } = useRanksWithTarget({
    subDepartmentUid: subDepartment?.uid,
  });

  const form = useForm<TargetsFormData>({
    resolver: zodResolver(targetsFormSchema),
    defaultValues: {
      ranksTargets: [],
    },
  });

  const getRanksTargets = () => {
    return form.getValues("ranksTargets");
  };

  const validateForm = () => {
    const ranksTargets = getRanksTargets();
    const errors: string[] = [];

    // Validation 1: At least 1 rank should have targets
    const ranksWithTargets = ranksTargets.filter((rankTarget) => {
      const hasPersonalTarget =
        rankTarget.personalTarget.m1 > 0 ||
        rankTarget.personalTarget.m2 > 0 ||
        rankTarget.personalTarget.m3 > 0;
      const hasMinimumTarget =
        rankTarget.minimumTarget.m1 > 0 ||
        rankTarget.minimumTarget.m2 > 0 ||
        rankTarget.minimumTarget.m3 > 0;
      return hasPersonalTarget || hasMinimumTarget;
    });

    if (ranksWithTargets.length === 0) {
      errors.push("At least one rank must have targets");
    }

    // Validation 2: Each rank with targets should have values for all 3 months
    ranksWithTargets.forEach((rankTarget) => {
      // Find rank title for better error messages
      const rankInfo = ranksWithTarget.find(
        (rank) => rank.uid === rankTarget.rankUId
      );
      const rankTitle = rankInfo?.title || rankTarget.rankUId;

      const hasPersonalTarget =
        rankTarget.personalTarget.m1 > 0 ||
        rankTarget.personalTarget.m2 > 0 ||
        rankTarget.personalTarget.m3 > 0;

      if (hasPersonalTarget) {
        if (
          rankTarget.personalTarget.m1 === 0 ||
          rankTarget.personalTarget.m2 === 0 ||
          rankTarget.personalTarget.m3 === 0
        ) {
          errors.push(
            `${rankTitle} personal target must have values for all 3 months`
          );
        }
      }

      const hasMinimumTarget =
        rankTarget.minimumTarget.m1 > 0 ||
        rankTarget.minimumTarget.m2 > 0 ||
        rankTarget.minimumTarget.m3 > 0;

      if (hasMinimumTarget) {
        if (
          rankTarget.minimumTarget.m1 === 0 ||
          rankTarget.minimumTarget.m2 === 0 ||
          rankTarget.minimumTarget.m3 === 0
        ) {
          errors.push(
            `${rankTitle} minimum target must have values for all 3 months`
          );
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const value = {
    targetType,
    setTargetType,
    subDepartment,
    setSubDepartment,
    form,
    getRanksTargets,
    validateForm,
  };

  return (
    <TargetsTemplateContext.Provider value={value}>
      {children}
    </TargetsTemplateContext.Provider>
  );
}

export function useTargetsTemplateContext() {
  const context = useContext(TargetsTemplateContext);

  if (context === undefined) {
    throw new Error(
      "useTargetsTemplateContext must be used within a TargetsTemplateProvider"
    );
  }

  return context;
}
